import {CommonModule} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeEmptyStateModule} from 'se-fe-empty-state';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeIconModule} from 'se-fe-icon';
import {Bundle, Product} from '@sportngin/se-commerce-sdk/models/clare';
import {
  ChooseBundleProductsModalComponent,
  ProductsSelectedEvent,
} from './choose-bundle-products-modal/choose-bundle-products-modal.component';
import {SeFeAlertModule} from 'se-fe-alert';
import {SetToActiveModalComponent} from './set-to-active-modal/set-to-active-modal.component';
import {isProductActive, Product as ClareProduct} from '../../../../models/clare';
import {BundleProductsTableComponent} from './bundle-products-table/bundle-products-table.component';

/**
 * Component for displaying and managing products included in a bundle.
 * Used in both bundle creation modal and bundle details page.
 */
@Component({
  selector: 'included-products-card',
  imports: [
    CommonModule,
    BundleProductsTableComponent,
    ChooseBundleProductsModalComponent,
    FormsModule,
    ReactiveFormsModule,
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeEmptyStateModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeIconModule,
    SetToActiveModalComponent,
  ],
  templateUrl: './included-products-card.component.html',
  styleUrls: ['./included-products-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncludedProductsCardComponent implements AfterViewInit, OnDestroy {
  bundle = input<Bundle>();
  error = input<string>();

  /**
   * Products included in the bundle.
   * Passed from parent component (create modal or details page).
   */
  products = input<ClareProduct[]>([]);
  loading = input<boolean>(false);

  /**
   * Mode determines the behavior:
   * - 'create': Used in create bundle modal (temporary state)
   * - 'edit': Used in bundle details page (persisted state)
   */
  mode = input<'create' | 'edit'>('create');

  /**
   * Bundle price value.
   * Only used in create mode.
   */
  bundlePrice = input<number>(0);

  ineligibleProducts: InputSignal<Product[]> = input<Product[]>([]);

  /**
   * Emitted when products are selected from the modal.
   * Parent component should handle storing the selected products.
   */
  productsSelected = output<ProductsSelectedEvent>();

  /**
   * Emitted when a product is removed from the bundle.
   * Parent component should handle the removal (update state or API call).
   */
  productRemoved = output<ClareProduct>();

  /**
   * Emitted when bundle price changes.
   * Parent component should update the bundle price form control.
   */
  bundlePriceChanged = output<number>();

  /**
   * Emitted when all products should be set to active.
   * Parent component should handle updating the products' active status.
   */
  setAllProductsToActive = output<ClareProduct[]>();

  /**
   * Emitted when a single product's active status is toggled.
   * Parent can handle by dispatching ProductListActions.toggleProductActiveStatus.
   */
  activeStatusToggled = output<{productId: string; active: boolean}>();

  chooseProductsModal = viewChild(ChooseBundleProductsModalComponent);
  setToActiveModal = viewChild(SetToActiveModalComponent);

  isBundleEmpty = computed(() => {
    const products = this.products();
    return !products || products.length === 0;
  });

  /**
   * Checks if any products in the bundle are inactive.
   * Returns true only if there are products and at least one is inactive.
   */
  hasInactiveProducts = computed(() => {
    const products = this.products();
    if (!products || products.length === 0) {
      return false;
    }
    return products.some(product => !isProductActive(product));
  });

  /**
   * Sum of each product's standalone (list) price in dollars — what customers would pay if they
   * bought the items separately — for comparison with the bundle price.
   */
  productPriceTotal = computed(() => {
    const products = this.products();
    if (!products || products.length === 0) {
      return 0;
    }
    return products.reduce((total, product) => {
      const addition = product.price;
      return total + (typeof addition === 'number' && !Number.isNaN(addition) ? addition : 0);
    }, 0);
  });

  bundlePriceForm: FormGroup; // Made public so parent(create bundle modal) can access it

  bundlePriceConfig: SeFeFormFieldConfig = {
    label: 'Bundle price',
    errorMessages: {
      required: 'Price is required',
      min: 'Price must be greater than 0',
      pattern: 'Price must be a valid number',
    },
  };

  cdr = inject(ChangeDetectorRef); // Made public so parent(create bundle modal) can trigger change detection
  private bundlePriceSubscription?: Subscription;

  constructor() {
    this.bundlePriceForm = new FormGroup({
      bundlePrice: new FormControl(null, [
        Validators.required,
        Validators.min(0),
        Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'), // only allow numbers and up to 2 decimal places
      ]),
    });

    // Watch for bundlePrice input changes and update form (only in create mode)
    effect(() => {
      if (this.mode() === 'create') {
        const inputPrice = this.bundlePrice();
        const priceControl = this.bundlePriceForm.get('bundlePrice');
        if (priceControl) {
          const currentFormValue = parseFloat(priceControl.value) || 0;
          // Only update if the input value is different to avoid circular updates
          if (Math.abs(currentFormValue - inputPrice) > 0.01) {
            priceControl.setValue(inputPrice.toString(), {emitEvent: false});
          }
        }
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    // Initialize bundle price form control with current value
    if (this.mode() === 'create') {
      // Defer patching so dev-mode "no changes" checks don't trip on validation/styling
      // updates from the SeFeFormField component.
      setTimeout(() => {
        // Subscribe to form changes to emit to parent
        this.bundlePriceSubscription?.unsubscribe();
        this.bundlePriceSubscription = this.bundlePriceForm.get('bundlePrice')?.valueChanges.subscribe(value => {
          const numValue = parseFloat(value) || 0;
          this.bundlePriceChanged.emit(numValue);
        });

        this.cdr.markForCheck();
      }, 0);
    } else {
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.bundlePriceSubscription?.unsubscribe();
  }

  /**
   * Handles toggling the active status of a product in the bundle.
   * Forwards to parent via activeStatusToggled so parent can dispatch the product toggle action.
   * @param event - Event containing productId and the new active status.
   */
  toggleActiveStatus(event: {productId: string; active: boolean}): void {
    this.activeStatusToggled.emit(event);
  }

  /**
   * Handles removing a product from the bundle.
   * @param product - The product to remove.
   */
  handleRemoveFromBundle(product: ClareProduct): void {
    this.productRemoved.emit(product);
  }

  /**
   * Handles products selected from the choose-bundle-products-modal.
   */
  handleProductsSelected(event: ProductsSelectedEvent): void {
    this.productsSelected.emit(event);
  }

  /**
   * Opens the choose products modal programmatically.
   */
  openChooseProductsModal(event: Event): void {
    const modal = this.chooseProductsModal();
    if (modal) {
      const triggerEl = (event.currentTarget as HTMLElement | null) ?? (event.target as HTMLElement | null);
      if (triggerEl) {
        modal.onOpenModal(triggerEl);
      }
    }
  }

  /**
   * Handles the event when all products should be set to active.
   * Emits the products to the parent component.
   */
  handleSetAllProductsToActive(): void {
    const products = this.products();
    if (products && products.length > 0) {
      this.setAllProductsToActive.emit(products);
    }
  }
}
